export const POPUP_BLOCKED_MESSAGE =
  "It looks like your browser blocked a popup. Please allow popups for this site to continue.";

export type PopupWindowNotify = (message: string, options?: any) => void;

export type PopupWindowOptions = {
  blockedMessage?: string;
  checkDelayMs?: number;
  onBlocked?: () => void;
  // A window already opened synchronously in direct response to the user's
  // click (e.g. window.open("", "_blank") before an await) — navigated via
  // location.href instead of calling window.open again here. A fresh
  // window.open call made after an await can miss the browser's "recent user
  // gesture" window and get blocked even though the click was genuine;
  // navigating an already-open window's location is not subject to that
  // check. Pass undefined (the default) to keep the plain window.open(url)
  // behavior.
  existingWindow?: Window | null;
};

export type PopupWindowResult = {
  blocked: boolean;
  windowRef: Window | null;
};

export const notifyPopupBlocked = (notify?: PopupWindowNotify) => {
  if (!notify) return undefined;

  return () =>
    notify(POPUP_BLOCKED_MESSAGE, {
      type: "warning",
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
};

export const isPopupBlocked = (windowRef?: Window | null): boolean => {
  if (!windowRef) {
    return true;
  }

  try {
    return windowRef.closed === true || typeof windowRef.closed === "undefined";
  } catch {
    return true;
  }
};

export const openWithPopupCheck = (
  url?: string | URL | null,
  target?: string,
  features?: string,
  options: PopupWindowOptions = {}
): PopupWindowResult => {
  const windowRef =
    options.existingWindow !== undefined
      ? options.existingWindow
      : window.open(url?.toString(), target, features);
  if (options.existingWindow !== undefined && !isPopupBlocked(windowRef)) {
    windowRef!.location.href = url?.toString() || "";
  }
  const message = options.blockedMessage || POPUP_BLOCKED_MESSAGE;
  const checkDelayMs = options.checkDelayMs ?? 200;
  let hasWarned = false;

  const warnBlocked = () => {
    if (hasWarned) return;
    hasWarned = true;

    if (options.onBlocked) {
      options.onBlocked();
      return;
    }

    window.alert(message);
  };

  const blocked = isPopupBlocked(windowRef);
  if (blocked) {
    warnBlocked();
  } else {
    window.setTimeout(() => {
      if (isPopupBlocked(windowRef)) {
        warnBlocked();
      }
    }, checkDelayMs);
  }

  return { blocked, windowRef };
};
